import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { SeoKeywordOpportunity, OpportunityStatus, FixGuidance, AiSuggestion, SeoTask } from '@/types/api/seo';

type Props = {
  opportunity: SeoKeywordOpportunity;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onGetFixGuidance: (opportunity: SeoKeywordOpportunity) => Promise<FixGuidance | null>;
  onGetSuggestions: (opportunity: SeoKeywordOpportunity) => Promise<AiSuggestion[] | null>;
  onApplyToWordPress: (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null) => Promise<{ success: boolean; error?: string }>;
  onAddTask: (suggestion: AiSuggestion, opportunityId: string) => Promise<boolean>;
  existingTasks: SeoTask[];
  wpConnected: boolean;
  rankingPageUrl: string | null;
  currentMetrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null };
};

const priorityColors: Record<string, string> = {
  high: '#EA4335',
  medium: '#F4B400',
  low: '#4285F4',
};

const difficultyColors: Record<string, string> = {
  easy: '#0D9D58',
  medium: '#F4B400',
  hard: '#EA4335',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  completed: 'Completed',
  dismissed: 'Dismissed',
};

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
  open: 'default',
  in_progress: 'warning',
  completed: 'success',
};


const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
      {copied ? <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} /> : <ContentCopyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
    </IconButton>
  );
};

const suggestionTypeLabels: Record<string, { color: string; bg: string }> = {
  title_tag: { color: '#1a0dab', bg: '#E8F0FE' },
  meta_description: { color: '#0D9D58', bg: '#E6F4EA' },
  h2_section: { color: '#054AD9', bg: '#FFF0F5' },
  paragraph: { color: '#5F6368', bg: '#F8F9FA' },
  faq_entry: { color: '#7C3AED', bg: '#F3E8FF' },
  internal_link: { color: '#4285F4', bg: '#E8F0FE' },
  redirect: { color: '#EA4335', bg: '#FCE8E6' },
  schema_markup: { color: '#F4B400', bg: '#FEF7E0' },
  custom: { color: '#5F6368', bg: '#F8F9FA' },
};

const wpApplicableTypes = ['title_tag', 'meta_description', 'h2_section', 'paragraph', 'faq_entry'];

const ApplyToWpButton = ({ suggestion, opportunityId, pageUrl, onApply, onApplied }: {
  suggestion: AiSuggestion;
  opportunityId: string;
  pageUrl: string | null;
  onApply: (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null) => Promise<{ success: boolean; error?: string }>;
  onApplied?: () => void;
}) => {
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleApply = async () => {
    if (!window.confirm(`Apply this ${suggestion.type.replace(/_/g, ' ')} to your WordPress site?`)) return;
    setState('loading');
    const result = await onApply(opportunityId, suggestion, pageUrl);
    if (result.success) {
      setState('success');
      onApplied?.();
    } else {
      setErrorMsg(result.error ?? 'Failed');
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };

  return (
    <Button
      size="small"
      onClick={handleApply}
      disabled={state === 'loading'}
      sx={{
        fontSize: 10,
        textTransform: 'none',
        minWidth: 0,
        px: 1,
        py: 0.25,
        color: state === 'success' ? 'success.main' : state === 'error' ? 'error.main' : '#21759B',
        borderColor: state === 'success' ? 'success.main' : state === 'error' ? 'error.main' : '#21759B',
      }}
      variant="outlined"
      title={state === 'error' ? errorMsg : 'Apply to WordPress'}
    >
      {state === 'loading' ? <CircularProgress size={10} /> : state === 'success' ? 'Applied!' : state === 'error' ? 'Failed' : 'Apply to WP'}
    </Button>
  );
};

const AddTaskButton = ({ suggestion, opportunityId, onAdd, onAdded }: {
  suggestion: AiSuggestion;
  opportunityId: string;
  onAdd: (suggestion: AiSuggestion, opportunityId: string) => Promise<boolean>;
  onAdded?: () => void;
}) => {
  const [state, setState] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleAdd = async () => {
    setState('loading');
    const ok = await onAdd(suggestion, opportunityId);
    if (ok) {
      setState('success');
      onAdded?.();
    } else {
      setState('idle');
    }
  };

  if (state === 'success') {
    return (
      <Chip label="Task added" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
    );
  }

  return (
    <Button
      size="small"
      startIcon={state === 'loading' ? <CircularProgress size={10} /> : <AddTaskIcon sx={{ fontSize: 14 }} />}
      onClick={handleAdd}
      disabled={state === 'loading'}
      sx={{
        fontSize: 10,
        textTransform: 'none',
        minWidth: 0,
        px: 1,
        py: 0.25,
        color: '#054AD9',
        borderColor: '#054AD9',
      }}
      variant="outlined"
    >
      Add Task
    </Button>
  );
};

const OpportunityCard = function ({ opportunity, onStatusChange, onGetFixGuidance, onGetSuggestions, onApplyToWordPress, onAddTask, existingTasks, wpConnected, rankingPageUrl, currentMetrics }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [loadingGuidance, setLoadingGuidance] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const opp = opportunity;

  // Build set of suggestion indices that already have tasks
  const existingTaskValues = useMemo(() => {
    const values = new Set<string>();
    for (const t of existingTasks) {
      if (t.suggestedValue && t.opportunityId === opp.id) {
        values.add(t.suggestedValue);
      }
    }
    return values;
  }, [existingTasks, opp.id]);

  const initialApplied = useMemo(() => {
    const indices = new Set<number>();
    if (opp.aiSuggestions) {
      opp.aiSuggestions.forEach((s, i) => {
        if (existingTaskValues.has(s.value)) indices.add(i);
      });
    }
    return indices;
  }, [opp.aiSuggestions, existingTaskValues]);

  const [appliedSuggestionIndices, setAppliedSuggestionIndices] = useState<Set<number>>(new Set());

  const handleGetGuidance = async () => {
    setLoadingGuidance(true);
    await onGetFixGuidance(opp);
    setLoadingGuidance(false);
  };

  const handleGetSuggestions = async () => {
    setLoadingSuggestions(true);
    await onGetSuggestions(opp);
    setLoadingSuggestions(false);
  };

  const guidance = opp.aiFixGuidance;

  return (
    <Box sx={{
      mb: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: expanded ? '#054AD930' : 'divider',
      backgroundColor: expanded ? '#FFFBFD' : '#fafafa',
      overflow: 'hidden',
      transition: 'all 0.2s',
    }}>
      {/* Collapsed header */}
      <Box
        sx={{
          p: 1.5,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          borderLeft: '4px solid',
          borderColor: priorityColors[opp.priority] ?? '#96979b',
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={opp.priority}
              size="small"
              sx={{
                fontSize: 9, height: 18, fontWeight: 700, textTransform: 'uppercase',
                color: '#fff', backgroundColor: priorityColors[opp.priority],
              }}
            />
            <Chip label={opp.opportunityType.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
            <Chip
              label={statusLabels[opp.status]}
              size="small"
              color={statusColors[opp.status] ?? 'default'}
              sx={{ fontSize: 9, height: 18 }}
            />
            {opp.impactScore > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, ml: 0.5 }}>
                Impact: {opp.impactScore}/100
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: 11, fontWeight: 600 }}>
              {opp.metric}
            </Typography>
          </Box>
          <Box sx={{ fontSize: 13, fontWeight: 600 }}>
            {opp.title}
          </Box>
          <Box sx={{ fontSize: 11, color: 'text.secondary' }}>
            {opp.description.length > 120 && !expanded ? opp.description.slice(0, 120) + '...' : opp.description}
          </Box>
        </Box>
        <IconButton size="small" sx={{ p: 0.5, mt: 0.25 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>

          {/* Full description */}
          <Box sx={{ fontSize: 12, mb: 2, color: 'text.secondary' }}>
            {opp.description}
          </Box>

          {/* Fix Guidance */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AutoFixHighIcon sx={{ fontSize: 16, color: '#054AD9' }} />
              <Typography variant="caption" fontWeight={700} color="#054AD9">How to Fix</Typography>
            </Box>
            {guidance ? (
              <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Chip label={guidance.difficulty} size="small" sx={{ fontSize: 10, height: 18, color: '#fff', backgroundColor: difficultyColors[guidance.difficulty] ?? '#96979b' }} />
                  <Chip label={guidance.timeEstimate} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                </Box>
                {guidance.steps.map((step, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                    <Box sx={{
                      width: 20, height: 20, borderRadius: '50%', backgroundColor: '#054AD9', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.1,
                    }}>
                      {i + 1}
                    </Box>
                    <Box component="span" sx={{ fontSize: 12 }}>{step}</Box>
                  </Box>
                ))}
                <Box sx={{ fontSize: 12, fontStyle: 'italic', color: 'text.secondary', mt: 1 }}>
                  Expected impact: {guidance.estimatedImpact}
                </Box>
              </Box>
            ) : (
              <Button
                size="small"
                startIcon={loadingGuidance ? <CircularProgress size={12} /> : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
                onClick={handleGetGuidance}
                disabled={loadingGuidance}
                sx={{ fontSize: 11, textTransform: 'none', color: '#054AD9' }}
              >
                {loadingGuidance ? 'Generating guidance...' : 'Generate AI Fix Guidance'}
              </Button>
            )}
          </Box>

          {/* AI Suggestions */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <LightbulbIcon sx={{ fontSize: 16, color: '#F4B400' }} />
              <Typography variant="caption" fontWeight={700} color="#F4B400">AI Suggestions</Typography>
              {!opp.aiSuggestions && (
                <Button
                  size="small"
                  startIcon={loadingSuggestions ? <CircularProgress size={12} /> : <LightbulbIcon sx={{ fontSize: 14 }} />}
                  onClick={handleGetSuggestions}
                  disabled={loadingSuggestions}
                  sx={{ fontSize: 10, textTransform: 'none', ml: 'auto', color: '#F4B400' }}
                >
                  {loadingSuggestions ? 'Generating...' : 'Generate Suggestions'}
                </Button>
              )}
              {opp.aiSuggestions && (
                <Button
                  size="small"
                  startIcon={loadingSuggestions ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                  onClick={handleGetSuggestions}
                  disabled={loadingSuggestions}
                  sx={{ fontSize: 10, textTransform: 'none', ml: 'auto' }}
                >
                  Regenerate
                </Button>
              )}
            </Box>

            {opp.aiSuggestions && opp.aiSuggestions.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {opp.aiSuggestions.filter((_, i) => !appliedSuggestionIndices.has(i) && !initialApplied.has(i)).length === 0 && (
                  <Box sx={{ p: 1.5, backgroundColor: '#E6F4EA', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="#0D9D58" fontWeight={600}>
                      All suggestions have been added as tasks.
                    </Typography>
                  </Box>
                )}
                {opp.aiSuggestions.map((s, i) => {
                  if (appliedSuggestionIndices.has(i) || initialApplied.has(i)) return null;
                  const typeStyle = suggestionTypeLabels[s.type] ?? suggestionTypeLabels.custom;
                  return (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: typeStyle.bg,
                        border: '1px solid',
                        borderColor: `${typeStyle.color}20`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                        <Chip
                          label={s.type.replace(/_/g, ' ')}
                          size="small"
                          sx={{ fontSize: 9, height: 18, color: typeStyle.color, borderColor: typeStyle.color }}
                          variant="outlined"
                        />
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                          {s.label}
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <CopyButton text={typeof s.value === 'string' ? s.value : JSON.stringify(s.value, null, 2)} />
                          <AddTaskButton
                            suggestion={s}
                            opportunityId={opp.id}
                            onAdd={onAddTask}
                            onAdded={() => setAppliedSuggestionIndices((prev) => new Set([...prev, i]))}
                          />
                        </Box>
                      </Box>

                      <Box sx={{
                        p: 1.25,
                        backgroundColor: '#fff',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 0.75,
                        fontFamily: s.type === 'schema_markup' ? 'monospace' : 'inherit',
                      }}>
                        <Box
                          sx={{
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: s.type === 'title_tag' ? '#1a0dab' : 'text.primary',
                            fontWeight: s.type === 'title_tag' ? 600 : 400,
                          }}
                        >
                          {typeof s.value === 'string' ? s.value : JSON.stringify(s.value, null, 2)}
                        </Box>
                      </Box>

                      <Box sx={{ fontSize: 10, fontStyle: 'italic', color: 'text.secondary' }}>
                        {s.reasoning}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : !opp.aiSuggestions && !loadingSuggestions ? (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                Generate AI suggestions to get specific, copy-paste ready changes for this opportunity.
              </Typography>
            ) : null}

            {loadingSuggestions && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
                <CircularProgress size={14} sx={{ color: '#F4B400' }} />
                <Typography variant="caption" color="text.secondary">Generating specific suggestions...</Typography>
              </Box>
            )}
          </Box>


          {/* Task-driven status indicator */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {existingTasks.filter((t) => t.opportunityId === opp.id).length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                {existingTasks.filter((t) => t.opportunityId === opp.id && t.status === 'done').length}/
                {existingTasks.filter((t) => t.opportunityId === opp.id).length} tasks done
              </Typography>
            )}
            {opp.status !== 'dismissed' && opp.status !== 'completed' && (
              <Button
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                onClick={() => onStatusChange(opp.id, 'dismissed')}
                sx={{ fontSize: 11, textTransform: 'none', color: 'text.secondary', ml: 'auto' }}
              >
                Dismiss
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default OpportunityCard;
